import {
    AfterViewInit, ChangeDetectorRef,
    Component, DestroyRef,
    EventEmitter, inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';
import {BehaviorSubject, combineLatestWith} from 'rxjs';
import {map} from 'rxjs/operators';
import * as R from 'ramda';
import {DatePipe} from '@angular/common';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {Item} from '@emm/core/models/item.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {ItemGroupingsFacadeService} from '@emm/management/services/item-groupings-facade.service';
import {ItemService} from '@emm/core/services/item.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Locale} from '@emm/core/models/locale.model';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {
    DynamicManagementFormComponent
} from '@emm/shared/components/dynamic-form/dynamic-management-form/dynamic-management-form.component';


const LEGEND_ITEMS = [{color: '#B5E9FD', label: 'Global Value'}, {color: '#EBF9FF', label: 'Overridden Value'}];
const DARK_MODE_LEGEND_ITEMS = [{color: '#4A4A4A', label: 'Global Value'}, {
    color: '#5B6770',
    label: 'Overridden Value'
}];
const SUBMIT_ORIGIN = 'panel';

@Component({
    selector: 'emm-item-duplication-form',
    templateUrl: './item-duplication-form.component.html',
    styleUrls: ['./item-duplication-form.component.scss'],
    standalone: false
})
export class ItemDuplicationFormComponent implements OnInit, AfterViewInit {
    @Input() selectedItem: Item;
    isDarkMode = false;
    attributesByGroup = new Map<string, BehaviorSubject<any>>();
    attributeGroups;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    isReadOnly$ = this.authFacade.isReadOnly$;
    disabled$ = this.isReadOnly$.pipe(combineLatestWith(this.itemsFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    form = this.itemsFacade.form$;
    fields = [];
    values = [];
    itemDetail$ = this.itemsFacade.itemDetail$;
    itemTag: string;
    locale: Locale;
    model: any;
    pipe = new DatePipe('en-US');
    renderForm = new BehaviorSubject<boolean>(false);
    selectedStatus: string;
    itemTypes = [];
    @Input() itemToCopy: any;
    @Input() allAttributes: any;
    @Input() previousAttributeGroups: AttributeGroup[];
    @Output() attributeGroupSelected = new EventEmitter<AttributeGroup[]>();
    @Output() cancelClicked = new EventEmitter<void>();
    @Output() submissionFinished = new EventEmitter<void>();
    @ViewChildren(DynamicManagementFormComponent) dynamicForms: QueryList<DynamicManagementFormComponent>;

    constructor(
        private authFacade: AuthFacadeService,
        private itemsFacade: ItemsFacadeService,
        private itemGroupingsFacade: ItemGroupingsFacadeService,
        private itemService: ItemService,
        private notificationService: NotificationService,
        public sharedFacade: SharedFacadeService,
        private changeDetector: ChangeDetectorRef,
        private api: ManagementApiService,
    ) {
    }


    ngOnInit(): void {
        this.sharedFacade.currentTheme$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(theme => {
            theme === 'dark-theme' ? this.isDarkMode = true : this.isDarkMode = false;
        });
        this.getItemTypes().then(() => {
            setTimeout(() => {
                this.getAttributeGroups()
            }, 500)
        });
        if (this.itemGroupingsFacade.itemDetail$.value !== null) {
            this.itemDetail$ = this.itemGroupingsFacade.itemDetail$;
        }
    }


    ngAfterViewInit() {
        this.dynamicForms.changes
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            this.changeDetector.detectChanges();
        });
    }

    cancel(): void {
        this.cancelClicked.next();
    }

    hasChanges(): boolean {
        let result = false;
        try {
            if (this.renderForm && this.dynamicForms) {
                this.dynamicForms.forEach(df => {
                    if (df.form.dirty) {
                        result = true;
                    }
                });
            }
        } catch (e) {
        }
        return result;
    }

    async destroyForm() {
        await this.renderForm.next(false);
    }

    initializeLocaleForm() {
        this.renderForm.next(true);
    }

    async getItemTypes() {
        if (!window.location.toString().includes('groupings')) {
            this.api.getAllItemTypesForItems()
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(itemTypes => {
                    this.itemTypes = itemTypes;
                });
        } else {
            this.api.getAllItemTypesForItemGroups()
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(itemTypes => {
                    this.itemTypes = itemTypes;
                });
        }
    }

    getAttributeGroups(): void {
        this.buildAttributes();
        this.api.getAllAttributeGroups()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res) => {
            this.attributeGroups = [{id: 'DUPLICATED', name: 'Duplicated Attributes'}];
            this.attributeGroups.forEach(group => {
                this.attributesByGroup.set(group.id, this.filterByAttributeGroup(group.id))
            });
        });
    }

    filterByAttributeGroup(filterAttributeGroup: string) {
        const filterByGroup$ = new BehaviorSubject<any>(null);
        for (const attribute in this.allAttributes) {
            if (this.allAttributes[attribute].attributeGroup !== 'DYNAMIC') {
                this.allAttributes[attribute].attributeGroup = 'DUPLICATED';
            }
        }
        filterByGroup$.next(this.allAttributes.filter(attribute => attribute.attributeGroup === filterAttributeGroup));
        return filterByGroup$
    }


    hasValue(attributeGroupId: string): boolean {
        const attributesByGroup = this.attributesByGroup.get(attributeGroupId)
        return attributesByGroup?.getValue().length !== 0
    }


    openPanel(status): void {
        this.selectedStatus = status;
        try {
            if (!this.isPreviousPanel(status)) {
                this.previousAttributeGroups.push(status);
            }
            this.attributeGroupSelected.next(this.previousAttributeGroups);
        } catch (e) {
        }
    }


    closePanel(status): void {
        this.selectedStatus = null;
        this.previousAttributeGroups.find((item, index) => item === status && this.previousAttributeGroups.splice(index, 1));
        this.attributeGroupSelected.next(this.previousAttributeGroups);
    }

    isPreviousPanel(status) {
        try {
            return this.previousAttributeGroups.some(i => i.name === status.name);
        } catch (e) {
            return false;
        }
    }

    buildAttributes(): void {
        const tagAttribute = {
            attributeGroup: 'DUPLICATED',
            dateUpdated: new Date().toISOString(),
            tag: 'TAG',
            name: 'Tag',
            jsonProperty: 'tag',
            description: 'Unique item identifier',
            options: null,
            type: 'TEXT',
            required: true,
            scope: 'GLOBAL',
            label: 'Tag',
            disabled: false
        };
        this.allAttributes.push(tagAttribute);
        const typeAttribute = {
            attributeGroup: 'DUPLICATED',
            dateUpdated: new Date().toISOString(),
            tag: 'TYPE',
            name: 'Type',
            jsonProperty: 'type',
            description: 'Item type',
            options: [],
            type: 'SELECT',
            required: true,
            scope: 'GLOBAL',
            label: 'Type',
            disabled: false
        };
        for (const itemType of this.itemTypes) {
            typeAttribute.options.push({label: itemType.name, value: itemType.tag});
        }
        this.allAttributes.push(typeAttribute);
        const lockedttribute = {
            attributeGroup: 'DUPLICATED',
            dateUpdated: new Date().toISOString(),
            tag: 'LOCKED',
            name: 'Locked',
            jsonProperty: 'locked',
            description: 'Unique item identifier',
            options: [{label: 'True', value: true}, {label: 'False', value: false}],
            type: 'SELECT',
            required: true,
            scope: 'GLOBAL',
            label: 'Locked',
            disabled: true,
            value: this.itemToCopy.locked
        };
        this.allAttributes.push(lockedttribute);
        this.transformItemAttributes().then(() => this.orderAttributes());
    }

    transformSelectAndBooleanAttributes() {
        const dropdownAttributeMap = new Map<string, any>();
        for (const attribute of this.allAttributes) {
            if (attribute.type === 'SELECT') {
                dropdownAttributeMap.set(attribute.tag, attribute);
            } else if (attribute.type === 'BOOL') {
                attribute.value = attribute.value === 'true' || attribute.value === true ? 'TRUE' : 'FALSE';
                attribute.options = [{label: 'True', value: 'TRUE'}, {label: 'False', value: 'FALSE'}];
            }
        }
        for (const attribute of this.allAttributes) {
            const matchingItem = dropdownAttributeMap.get(attribute.tag);
            if (matchingItem && attribute.tag === matchingItem.attribute) {
                matchingItem.value = attribute.value;
            }
        }
    }
    async transformMultiSelectAttributes() {
        const multiSelectAttributeMap = new Map<string, any>();
        for (const attribute of this.allAttributes) {
            if (attribute.type === 'MULTI_SELECT') {
                attribute.value = attribute.value.split(',').map(option => option.trim());
                multiSelectAttributeMap.set(attribute.tag, attribute);
            }
        }
        for (const attribute of this.allAttributes) {
            const matchingItem = multiSelectAttributeMap.get(attribute.tag);
            if (matchingItem && attribute.tag === matchingItem.attribute) {
                matchingItem.value = attribute.value;
            }
        }
        await console.log('MultiSelect Attribute Values Transformed')
    }

    transformAllAttributes() {
        // this copies the itemToCopy attribute values into the allAttributes array for further transformation before
        // rendering the form
        const itemAttributes = this.itemToCopy?.attributes;
        const itemAttributeMap = new Map<string, any>();
        if (itemAttributes) {
            for (const itemAttribute of itemAttributes) {
                itemAttributeMap.set(itemAttribute.attribute, itemAttribute);
            }
        }
        for (const attribute of this.allAttributes) {
            const matchingItem = itemAttributeMap.get(attribute?.tag);
            if (matchingItem) {
                attribute.value = matchingItem.value;
            }
        }
    }


    async transformItemAttributes() {
        this.transformAllAttributes();
        this.transformMultiSelectAttributes().then(() => this.transformSelectAndBooleanAttributes());
        await this.notificationService.success(`Successfully copied attributes from ${this.itemToCopy.tag}`);
    }

    orderAttributes() {
        let orderedRequiredAttributes = [];
        for (const attribute of this.allAttributes) {
            switch (attribute.tag) {
                case 'NAME':
                    orderedRequiredAttributes[0] = attribute;
                    break;
                case 'TAG':
                    orderedRequiredAttributes[1] = attribute;
                    break;
                case 'TYPE':
                    orderedRequiredAttributes[2] = attribute;
                    break;
                case 'LOCKED':
                    orderedRequiredAttributes[3] = attribute;
                    break;
                case 'DAY_PART':
                    orderedRequiredAttributes[4] = attribute;
                    break;
                case 'DISABLED':
                    orderedRequiredAttributes[5] = attribute;
                    break;
                case 'DOT_COM_VISIBLE':
                    orderedRequiredAttributes[6] = attribute;
                    break;
                case 'ITEM_GROUP_TYPE':
                    orderedRequiredAttributes[7] = attribute;
                    break;
                case 'STATUS':
                    orderedRequiredAttributes[8] = attribute;
                    break;
                case 'POS_NUTRITION_CATEGORY':
                    orderedRequiredAttributes[9] = attribute;
                    break;
                case 'SALT_LAW':
                    orderedRequiredAttributes[10] = attribute;
                    break;
            }
        }
        const nonRequiredAttributes = this.allAttributes.filter(attribute => attribute.required !== true);
        orderedRequiredAttributes = orderedRequiredAttributes.concat(nonRequiredAttributes);
        this.allAttributes = orderedRequiredAttributes;
        this.initializeLocaleForm();
    }

    getAllValues(form: any): {} {
        const allValues = [];
        if (form) {
            Object.keys(form.controls).forEach((key) => {
                const currentControl = form.controls[key];
                currentControl.markAsDirty();
                for (const attribute of this.allAttributes) {
                    if (attribute.jsonProperty === key) {
                        currentControl.tag = attribute.tag;
                    }
                }
                allValues.push({tag: currentControl.tag, value: currentControl.value});
            });
            const hasName = allValues.some((value) => value.tag === 'NAME');
            if (!hasName) {
                allValues.push({tag: 'NAME', value: this.dynamicForms.first.form.controls['name'].value});
            }
        }

        return allValues;
    }

    getAllAttributes() {
        let mergedUpdates;
        let newUpdates;
        this.dynamicForms
            .forEach(df => {
                newUpdates = {...newUpdates, ...this.getAllValues(df.form)}
                mergedUpdates = {...mergedUpdates, ...newUpdates};
            });
        return mergedUpdates === undefined ? newUpdates : mergedUpdates;
    }


    submit(reloadAfterUpdate: boolean = true): void {
        if (this.hasInvalidFormfield()) {
            this.notificationService.error(`Please fill out all required fields.`);
            return;
        }
        let mergedUpdates = {...this.getAllAttributes()};
        mergedUpdates = Object.values(mergedUpdates).filter(value => value['tag'] !== 'TAG' &&
            value['tag'] !== 'TYPE' && value['tag'] !== 'LOCKED');
        const mergedUpdatesArray = [];
        Object.values(mergedUpdates).forEach((value) => mergedUpdatesArray.push(value));
        this.itemService.saveItem({
            tag: this.dynamicForms.first.form.controls['tag'].value,
            itemType: this.dynamicForms.first.form.controls['type'].value,
            locked: this.itemToCopy.locked,
            attributes: mergedUpdatesArray,
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                    next: (res) => {
                        this.notificationService.success(`New item successfully created`);
                        this.submissionFinished.next();
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
    }

    hasInvalidFormfield() {
        let result;
        if (this.renderForm && this.dynamicForms) {
            result = this.dynamicForms?.first?.form?.invalid;
        }
        return result ?? false;
    }
}
